import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groomingService, GroomingFilters, GroomingAppointmentCreate, GroomingAppointmentUpdate } from '@/services/grooming';

interface UseGroomingOptions {
  enabled?: boolean;
}

export function useGroomingAppointments(filters?: GroomingFilters, options?: UseGroomingOptions) {
  const { enabled = true } = options || {};
  return useQuery({
    queryKey: ['grooming', 'appointments', filters],
    queryFn: () => groomingService.getAppointments(filters),
    enabled,
  });
}

export function useTodayGroomingAppointments(options?: UseGroomingOptions) {
  const { enabled = true } = options || {};
  const today = new Date().toISOString().split('T')[0];
  return useQuery({
    queryKey: ['grooming', 'today', today],
    queryFn: () => groomingService.getAppointments({ date_from: today, date_to: today }),
    enabled,
    refetchInterval: 60000, // Refrescar cada minuto
  });
}

export function useGroomingCalendar(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: ['grooming', 'calendar', dateFrom, dateTo],
    queryFn: () => groomingService.getCalendar(dateFrom, dateTo),
    enabled: !!dateFrom && !!dateTo,
  });
}

export function useGroomingAppointment(id: number | null) {
  return useQuery({
    queryKey: ['grooming', 'appointment', id],
    queryFn: () => groomingService.getAppointment(id!),
    enabled: !!id,
  });
}

export function useCreateGroomingAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GroomingAppointmentCreate) => groomingService.createAppointment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grooming'] });
    },
  });
}

export function useUpdateGroomingAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: GroomingAppointmentUpdate }) =>
      groomingService.updateAppointment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grooming'] });
    },
  });
}

export function useCompleteGroomingAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => groomingService.completeAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grooming'] });
      // Los reportes (cancelaciones por cliente, turnos del periodo) viven bajo otra
      // clave: sin esto el turno cambiaba de estado pero el reporte seguia mostrando
      // el numero cacheado.
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useCancelGroomingAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => groomingService.cancelAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grooming'] });
      // Los reportes (cancelaciones por cliente, turnos del periodo) viven bajo otra
      // clave: sin esto el turno cambiaba de estado pero el reporte seguia mostrando
      // el numero cacheado.
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useDeleteGroomingAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => groomingService.deleteAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grooming'] });
      // Los reportes (cancelaciones por cliente, turnos del periodo) viven bajo otra
      // clave: sin esto el turno cambiaba de estado pero el reporte seguia mostrando
      // el numero cacheado.
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}
